import { Property, SystemTranslation, ViewerTranslations } from '@common/entities';
import { get } from 'config/api/config';

const VIEWER_TRANSLATIONS_ENDPOINT = '/viewer/applications';
const VIEWER_PUBLIC_ENDPOINT = '/viewer/public';

/** Normalize translations to Record - API/Prisma may return [] for empty JSONB */
function normalizeTranslations(value: unknown): Record<string, string> {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, string>;
  }
  return {};
}

export const getViewerTranslations = async (
  applicationId: string,
  localeCode: string,
  versionId?: string,
): Promise<ViewerTranslations> => {
  const url = versionId
    ? `${VIEWER_TRANSLATIONS_ENDPOINT}/${applicationId}/translations/${localeCode}?versionId=${encodeURIComponent(versionId)}`
    : `${VIEWER_TRANSLATIONS_ENDPOINT}/${applicationId}/translations/${localeCode}`;
  const response = await get(url);
  const data = response?.data as ViewerTranslations | undefined;
  if (!data) {
    return {
      form: { localeCode, localeName: localeCode, translations: {} },
      system: { localeCode, localeName: localeCode, translations: {} },
    };
  }
  return {
    form: {
      localeCode: data.form?.localeCode ?? localeCode,
      localeName: data.form?.localeName ?? localeCode,
      translations: normalizeTranslations(data.form?.translations),
    },
    system: {
      localeCode: data.system?.localeCode ?? localeCode,
      localeName: data.system?.localeName ?? localeCode,
      translations: normalizeTranslations(data.system?.translations),
    },
  };
};

export const getViewerRouteTranslations = async (
  routeId: string,
  localeCode: string,
  versionId?: string,
): Promise<ViewerTranslations> => {
  const url = versionId
    ? `/application-routes/${routeId}/translations/${localeCode}?versionId=${encodeURIComponent(versionId)}`
    : `/application-routes/${routeId}/translations/${localeCode}`;
  const response = await get(url);
  const data = response?.data as ViewerTranslations | undefined;
  if (!data) {
    return {
      form: { localeCode, localeName: localeCode, translations: {} },
      system: { localeCode, localeName: localeCode, translations: {} },
    };
  }
  return {
    form: {
      localeCode: data.form?.localeCode ?? localeCode,
      localeName: data.form?.localeName ?? localeCode,
      translations: normalizeTranslations(data.form?.translations),
    },
    system: {
      localeCode: data.system?.localeCode ?? localeCode,
      localeName: data.system?.localeName ?? localeCode,
      translations: normalizeTranslations(data.system?.translations),
    },
  };
};

/**
 * Fetches property-scoped system translations for pre-application screens
 * (login, signup, forgot-password, entry). No authentication required.
 * Returns an empty translations object on error so auth screens degrade gracefully.
 */
export const getViewerPropertySystemTranslations = async (
  propertyId: number,
  localeCode: string,
): Promise<Record<string, string>> => {
  try {
    const url = `${VIEWER_PUBLIC_ENDPOINT}/properties/${propertyId}/translations/${localeCode}`;
    const response = await get(url);
    const data = response?.data as SystemTranslation | undefined;
    return normalizeTranslations(data?.translations);
  } catch {
    return {};
  }
};

/**
 * Fetches the languages configured for a property. No authentication required.
 * Returns an empty array on error so the language switcher degrades gracefully.
 */
export const getViewerPropertyLanguages = async (
  orgId: number,
  propertyId: number,
): Promise<Property | null> => {
  try {
    const url = `${VIEWER_PUBLIC_ENDPOINT}/properties/${orgId}/${propertyId}/languages`;
    const response = await get(url);
    return (response?.data as Property | undefined) ?? null;
  } catch {
    return null;
  }
};
